import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { GameType } from '~types/games';
import { TEN_PER_PAGE } from '~utils/const';

// Negative = days ago, Positive = days in future
function getDateRange(
    startDays: number,
    endDays: number,
    format: 'YYYY-MM-DD' | 'ISO' = 'YYYY-MM-DD'
) {
    const start = moment().add(startDays, 'days');
    const end = moment().add(endDays, 'days');

    switch (format) {
        case 'ISO':
            return {
                startDate: start.toISOString(),
                endDate: end.toISOString(),
            };
        default:
            return {
                startDate: start.format('YYYY-MM-DD'),
                endDate: end.format('YYYY-MM-DD'),
            };
    }
}

// TODO: Is it normal to have some ISO and some YYYY-MM-DD formats? Should we align?
const last_week_iso = () => getDateRange(-7, 0, 'ISO');
const last_3_months_iso = () => getDateRange(-90, 0, 'ISO');
const last_month = () => getDateRange(-30, 0);

export const useNavigation = () => {
    const history = useHistory();

    return useMemo(
        () => ({
            // Home
            goToHome() {
                history.push('/');
            },
            // Featured
            replaceToFeaturedList() {
                history.replace(`/featured?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToFeaturedList() {
                history.push(`/featured?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToFeaturedReordering() {
                history.push('/featured-reordering');
            },
            replaceToFeaturedCreation() {
                history.replace('/featured/create');
            },
            goToFeaturedCreate() {
                history.push('/featured/create');
            },
            goToFeaturedEdit(id: number) {
                history.push(`/featured/edit/${id}`);
            },
            // Client
            goToClients(q = '') {
                history.push(`/clients?page=0&perPage=${TEN_PER_PAGE}&q=${q}`);
            },
            // Analytics
            goToUsersAnalytics() {
                history.push('/analytics/users');
            },
            goToCarwashAnalytics() {
                history.push('/analytics/carwash');
            },
            goToAnalyticsOverview() {
                history.push('/analytics/overview');
            },
            // Taxi driver campaign dashboard (throwaway).
            goToTaxiAnalytics() {
                history.push('/analytics/taxi');
            },
            // NHL Section
            goToNhlGames() {
                history.push(`/nhl-games?page=0&perPage=${TEN_PER_PAGE}`);
            },
            replaceToNhlGame() {
                history.replace(`/nhl-games?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToNhlGamesDashboard() {
                const { startDate, endDate } = last_3_months_iso();
                history.push(
                    `/nhl-games-dashboard?page=0&perPage=${TEN_PER_PAGE}&startDate=${startDate}&endDate=${endDate}&limit=32`
                );
            },
            goToOnboarding() {
                const { startDate, endDate } = last_month();
                history.push(
                    `/onboarding?startDate=${startDate}&endDate=${endDate}`
                );
            },
            goToNhlGamesLeaderboard() {
                history.push(`/nhl-games-leaderboard?page=0&perPage=20`);
            },
            goToNhlGameEdit(id: number) {
                history.push(`/nhl-games/edit/${id}?limit=10`);
            },
            goToNhlGameCreate() {
                history.push(`/nhl-games/create`);
            },
            // Feedback
            goToFeedbacks() {
                const { startDate, endDate } = last_3_months_iso();
                history.push(
                    `/feedbacks?page=0&perPage=${TEN_PER_PAGE}&startDate=${startDate}&endDate=${endDate}`
                );
            },
            // Config
            goToConfig() {
                history.push('/general-configs');
            },
            // Coupons
            goToCoupons() {
                history.push(`/coupons?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToCouponsInStore() {
                history.push(
                    `/in-store-promotions?page=0&perPage=${TEN_PER_PAGE}`
                );
            },
            replaceToCoupons() {
                history.replace(`/coupons?page=0&perPage=${TEN_PER_PAGE}`);
            },
            replaceToCouponsInStore() {
                history.replace(
                    `/in-store-promotions?page=0&perPage=${TEN_PER_PAGE}`
                );
            },
            goToCouponEdit(id: number) {
                history.push(`/coupon/edit/${id}`);
            },
            goToInStoreCouponEdit(id: number) {
                history.push(`/in-store-promotion/edit/${id}`);
            },
            goToCouponCreate() {
                history.push(`/coupon/create`);
            },
            goToInStoreCouponCreate() {
                history.push(`/in-store-promotion/create`);
            },
            goToCouponDuplicate(id: number) {
                history.push(`/coupon/duplicate/${id}`);
            },
            goToInStoreCouponDuplicate(id: number) {
                history.push(`/in-store-promotion/duplicate/${id}`);
            },
            replaceToCouponCreate() {
                history.push(`/coupon/create`);
            },
            replaceToInStoreCouponCreate() {
                history.push(`/in-store-promotion/create`);
            },
            // Coupon grants
            goToGrants(id: number) {
                history.push(`/coupon/${id}/grants`);
            },
            // Stores
            goToStores() {
                history.push('/stores');
            },
            goToStoresDiff() {
                history.push('/stores-diff');
            },
            replaceToStores() {
                history.replace('/stores');
            },
            // Games
            goToGames() {
                history.push('/games');
            },
            replaceToGames() {
                history.replace('/games');
            },
            goToGameEdit(id: number, type: GameType) {
                history.push(`/game-${type}/edit/${id}`);
            },
            goToGameCreate(type: GameType) {
                history.push(`/game-${type}/create`);
            },
            // Categories
            goToCategories() {
                history.push(`/categories?page=0&perPage=${TEN_PER_PAGE}`);
            },
            replaceToCategories() {
                history.replace(`/categories?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToCategoryEdit(id: number) {
                history.push(`/category/edit/${id}`);
            },
            goToCategoryCreate() {
                history.push(`/category/create`);
            },
            replaceToCategoryCreate() {
                history.replace(`/category/create`);
            },
            // Offers
            goToOffers() {
                history.push(`/offers?page=0&perPage=${TEN_PER_PAGE}`);
            },
            replaceToOffers() {
                history.replace(`/offers?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToOfferEdit(id: number) {
                history.push(`/offer/edit/${id}`);
            },
            goToOfferCreate() {
                history.push(`/offer/create`);
            },
            replaceToOfferCreate() {
                history.replace(`/offer/create`);
            },
            // Loyalty Clubs
            goToLoyaltyClubs() {
                history.push(`/loyalty-clubs?page=0&perPage=${TEN_PER_PAGE}`);
            },
            replaceToLoyaltyClubs() {
                history.replace(
                    `/loyalty-clubs?page=0&perPage=${TEN_PER_PAGE}`
                );
            },
            goToLoyaltyClubEdit(id: number) {
                history.push(`/loyalty-club/edit/${id}`);
            },
            goToLoyaltyClubCreate() {
                history.push(`/loyalty-club/create`);
            },
            replaceToLoyaltyClubEdit(id: number) {
                history.replace(`/loyalty-club/edit/${id}`);
            },
            // Road to Rewards (singleton campaign — no id in the URL)
            goToRoadToRewardsAnalytics() {
                history.push('/road-to-rewards/analytics');
            },
            goToRoadToRewardsConfiguration() {
                history.push('/road-to-rewards/configuration');
            },
            // NHL Coins: no period, the page defaults to the edition's window
            goToNhlCoinsAnalytics() {
                history.push('/nhl-coins/analytics');
            },
            // Loyalty Banners
            goToLoyaltyBanners() {
                history.push(`/loyalty-banners?page=0&perPage=${TEN_PER_PAGE}`);
            },
            replaceToLoyaltyBanners() {
                history.replace(
                    `/loyalty-banners?page=0&perPage=${TEN_PER_PAGE}`
                );
            },
            goToLoyaltyBannerEdit(id: number) {
                history.push(`/loyalty-banner/edit/${id}`);
            },
            goToLoyaltyBannerCreate() {
                history.push(`/loyalty-banner/create`);
            },
            replaceToLoyaltyBannerCreate() {
                history.replace(`/loyalty-banner/create`);
            },
            goToProfile() {
                history.push('/profile');
            },
            goToAppPreview() {
                history.push('/app-preview');
            },
            goToCareers() {
                history.push('/careers');
            },
            goToCareerEdit(id: number) {
                history.push(`/career/edit/${id}`);
            },
            goToCareerCreate() {
                history.push('/career/create');
            },
            goToRechargesImages() {
                history.push('/recharge');
            },
            goToRechargeEdit(id: number) {
                history.push(`/recharge/images/edit/${id}`);
            },
            goToRechargeCreate() {
                history.push('/recharge/images/create');
            },
            // RPP dashboard
            goToRppDashboard() {
                history.push('/rpp-dashboard');
            },
            // RPP ads
            goToRppAds() {
                history.push(`/rpp-ads?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToRppAdCreate() {
                history.push('/rpp-ads/create');
            },
            goToRppAdEdit(id: number) {
                history.push(`/rpp-ads/edit/${id}`);
            },
            // RPP prize coupons
            goToRppPrizeCoupons() {
                history.push(
                    `/rpp-prize-coupons?page=0&perPage=${TEN_PER_PAGE}`
                );
            },
            goToRppPrizeCouponCreate() {
                history.push('/rpp-prize-coupons/create');
            },
            goToRppPrizeCouponEdit(id: number) {
                history.push(`/rpp-prize-coupons/edit/${id}`);
            },
            goToContests() {
                history.push(`/contests?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToContestCreate() {
                history.push('/contest/create');
            },
            goToContestEdit(id: number, tab?: string) {
                history.push(`/contest/edit/${id}${tab ? `/${tab}` : ''}`);
            },
            replaceToContestEdit(id: number, tab?: string) {
                history.replace(`/contest/edit/${id}${tab ? `/${tab}` : ''}`);
            },
            // 31 Days of Circle K
            goToDoc31() {
                history.push('/31doc');
            },
            goToCarWash() {
                history.push('/car-wash');
            },
            goToLabattGamesDashboard() {
                const { startDate, endDate } = last_week_iso();
                history.push(
                    `/labatt-games-dashboard?startDate=${startDate}&endDate=${endDate}`
                );
            },
            // Events
            goToEvents() {
                history.push(`/events?page=0&perPage=${TEN_PER_PAGE}`);
            },
            goToEventEdit(id: number) {
                history.push(`/event/edit/${id}`);
            },
            goToEventCreate() {
                history.push(`/event/create`);
            },
        }),
        [history]
    );
};

export type Navigation = ReturnType<typeof useNavigation>;
